import { Link, useParams } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { COLORS, COLOR_LIST } from "@/data/colors";
import { useSeo } from "@/hooks/use-seo";
import { motion } from "framer-motion";
import NotFound from "@/pages/not-found";
import { ShareButtons } from "@/components/social/ShareButtons";
import { AdSlot } from "@/components/ads/AdSlot";
import { classifyAuraFamily, AURA_READS } from "@/lib/aura-extract";
import { ColorSpecs } from "@/components/ColorSpecs";

const FAMILY_LABEL: Record<string, string> = {
  warm: "Warm",
  cool: "Cool",
  growth: "Growth",
  mystic: "Mystic",
  neutral: "Neutral",
};

// One contextual introduction per family, shown at the top of every color page.
const FAMILY_INTRO: Record<string, string> = {
  warm: "Warm colors — commonly reds, oranges, and yellows — can bring visual emphasis to a composition, especially through contrast with cooler or less saturated colors. Designers often use them for accents, highlights, and focal points. The emotional associations are not fixed: studies find some color-emotion patterns shared across groups as well as differences between countries and languages. A warm hue alone does not guarantee urgency, appetite, or faster decisions; consider the particular shade, setting, and audience.",
  growth: "Green and neighboring hues appear across leaves, grass, moss, and many cultivated landscapes, which makes them familiar choices for designs about plants, food, or environmental topics. Seeing green in a graphic or painted room is not the same as spending time in nature, and claims about stress or health should not be transferred between those settings without evidence. Color-emotion studies find both shared associations and cultural variation, so use green as a design cue to explore rather than a guaranteed signal of calm or wellbeing.",
  cool: "Cool colors — commonly blues, blue-greens, and blue-grays — can create a different visual balance from warm accents, depending on contrast, lightness, and surrounding colors. Blue is a familiar choice in many institutional and digital identities, but that convention does not guarantee trust or competence. Research finds associations between color and emotion, alongside variation between groups and contexts. Use a cool palette to support a visual direction, then check its legibility and meaning with the people who will see it.",
  mystic: "Violet and purple tones cover a broad range of hues, from reddish plum to bluish violet. Their meanings in design, fashion, and ceremony vary across communities and historical periods; no single symbolic reading applies everywhere. Color-emotion research finds patterns as well as cultural differences, but it does not establish that purple inherently produces creativity, luxury, or spiritual depth. For a practical palette, decide whether the specific shade supports the mood and contrast you want, and treat symbolism as context-dependent.",
  neutral: "Neutral palettes include black, white, gray, and many earthy tones, each with different contrasts and undertones. Designers often use them as backgrounds or foundations because they can leave room for other elements, but neutrality does not guarantee authority, status, or focus. Research on color describes associations rather than a universal effect for every shade. Consider the material, lighting, surrounding colors, and audience when choosing a neutral palette.",
};

export default function ColorDetail() {
  const { id } = useParams<{ id: string }>();
  const color = id ? COLORS[id] : undefined;

  const ogImage = color
    ? `https://mychromamind.com/og/${color.id}.jpg`
    : undefined;

  useSeo({
    title: color ? `${color.name} — Meaning & Psychology | Mychromamind` : "Color not found | Mychromamind",
    description: color
      ? `${color.name}: ${color.meaning}`
      : "This color could not be found.",
    path: color ? `/learn/${color.id}` : "/learn",
    ogType: color ? "article" : "website",
    ogImage,
    structuredData: color
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: `${color.name} — Color Meaning & Psychology`,
              description: color.meaning,
              about: color.keywords.join(", "),
              datePublished: "2026-07-01",
              dateModified: "2026-07-01",
              image: `https://mychromamind.com/og/${color.id}.jpg`,
              author: {
                "@type": "Person",
                name: "Daryoush Torabi Jafroudi",
                url: "https://mychromamind.com/about",
              },
              publisher: {
                "@type": "Organization",
                name: "Mychromamind",
                url: "https://mychromamind.com",
              },
              mainEntityOfPage: `https://mychromamind.com/learn/${color.id}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://mychromamind.com/" },
                { "@type": "ListItem", position: 2, name: "Learn", item: "https://mychromamind.com/learn" },
                { "@type": "ListItem", position: 3, name: color.name, item: `https://mychromamind.com/learn/${color.id}` },
              ],
            },
          ],
        }
      : undefined,
    noindex: !color,
  });

  if (!color) {
    return <NotFound />;
  }

  const idx = COLOR_LIST.findIndex((c) => c.id === color.id);
  const prev = COLOR_LIST[(idx - 1 + COLOR_LIST.length) % COLOR_LIST.length];
  const next = COLOR_LIST[(idx + 1) % COLOR_LIST.length];

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-24 w-full">
        <Link
          href="/learn"
          className="text-sm text-muted-foreground hover:text-primary transition-colors mb-8 inline-flex items-center gap-2"
        >
          ← Back to the color library
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-6"
        >
          <div
            className="w-full h-56 md:h-72 rounded-3xl shadow-sm border border-black/5 dark:border-white/5 mb-8"
            style={{ backgroundColor: color.hex }}
          />

          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
              {FAMILY_LABEL[color.family]} · {color.hex}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-serif mb-6">{color.name}</h1>

          <div className="flex flex-wrap gap-2 mb-8">
            {color.keywords.map((kw) => (
              <span
                key={kw}
                className="px-3 py-1 rounded-full bg-muted text-foreground border border-border text-xs font-medium tracking-wide"
              >
                {kw}
              </span>
            ))}
          </div>

          {/* Family intro — topical context for all colors in this family */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-8 border-l-2 border-border pl-4">
            {FAMILY_INTRO[color.family]}
          </p>

          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-serif mb-2">What it says about you</h2>
              <p className="text-muted-foreground leading-relaxed">{color.meaning}</p>
            </div>
            <div>
              <h2 className="text-lg font-serif mb-2">In a decision</h2>
              <p className="text-muted-foreground leading-relaxed">{color.decisionMeaning}</p>
            </div>
            <div>
              <h2 className="text-lg font-serif mb-2">The psychology of {color.name.toLowerCase()}</h2>
              <p className="text-muted-foreground leading-relaxed">{color.psychology}</p>
              <div className="mt-3">
                <h3 className="text-xs font-medium text-muted-foreground mb-1">Research and further reading</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {color.researchSources.map((source) => (
                    <li key={source.url}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary hover:underline underline-offset-4"
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-serif mb-2">In design and culture</h2>
              <p className="text-muted-foreground leading-relaxed">{color.usage}</p>
            </div>
            <div>
              <h2 className="text-lg font-serif mb-2">In the natural world</h2>
              <p className="text-muted-foreground leading-relaxed">{color.associations}</p>
            </div>

            {/* Aura profile */}
            {(() => {
              const auraFamily = classifyAuraFamily(color.hex);
              const aura = AURA_READS[auraFamily];
              const excerpt = aura.description.split(". ").slice(0, 2).join(". ") + ".";
              return (
                <div className="rounded-2xl border border-border bg-card/60 p-5">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                    Aura Profile
                  </p>
                  <h2 className="text-lg font-serif mb-2">{aura.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{excerpt}</p>
                  <Link
                    href="/aura-scanner"
                    className="mt-3 inline-block text-xs text-primary hover:underline underline-offset-4"
                  >
                    Scan a photo to get your own aura read →
                  </Link>
                </div>
              );
            })()}
          </div>

          <ColorSpecs hex={color.hex} id={color.id} name={color.name} />

          <AdSlot
            slotId="2431849490"
            content={[color.meaning, color.decisionMeaning, color.psychology, color.usage, color.associations]}
            className="mt-10"
          />

          <ShareButtons
            label={`Share ${color.name}`}
            text={`${color.name}: ${color.meaning}`}
            className="mt-10 pt-8 border-t border-border"
          />
        </motion.article>

        <div className="mt-16 pt-8 border-t border-border flex items-center justify-between text-sm">
          <Link href={`/learn/${prev.id}`} className="flex items-center gap-2 hover:text-primary transition-colors">
            <span
              className="w-6 h-6 rounded-full border border-black/10 dark:border-white/10"
              style={{ backgroundColor: prev.hex }}
            />
            {prev.name}
          </Link>
          <Link href={`/learn/${next.id}`} className="flex items-center gap-2 hover:text-primary transition-colors">
            {next.name}
            <span
              className="w-6 h-6 rounded-full border border-black/10 dark:border-white/10"
              style={{ backgroundColor: next.hex }}
            />
          </Link>
        </div>
      </div>
    </Shell>
  );
}
